import * as React from "react"
import { styled, ThemeProvider } from "@mui/material/styles"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import { useAppDispatch, useAppSelector } from "../../../util/hooks"
import {
  activeTabSelector,
  closeDialog,
  gantChartAsync,
  getParameterData,
  lastRunAsync,
  modelOpenSelector,
  newPipeLineUpdateBaseDataAsync,
  pipelineCardsAsync,
  refreshPipeLineAsync,
  refreshStatusPipeLineAsync,
  runPipeLineAsync,
  schedulePipeLineAsync,
  setAlertData,
  setAlertData2,
  toggleNewPipeLineScreen,
  updateParametersForPipelineAsync,
  updateRefreshStatus,
  updateTabIndex,
} from "../MonitorSlice"
import {
  ALERT_MSGS,
  ALERT_STATUS_MSGS,
  API_STATUS,
} from "../../../util/StaticData"
import { createUseStyles, useTheme } from "react-jss"
import { Backdrop, Box, CircularProgress, Tab, Tabs } from "@mui/material"
import {
  getDate,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isPast,
} from "date-fns"
import {
  findIndexByKey,
  flattenParamsToKeyValue,
  inputSeriesDateConversionBeforeSave,
} from "../../../util/CommonMethods"
import { NewPipeLineDetails } from "./NewPipeLineDetails"
import { redThemedTabs } from "./NewPipeLineTableWrapper"
import ScheduleTheRun from "./ScheduleRun"
import {
  NEW_PIPELINE_SCREENS,
  newPipeLineMapper,
  NewPipeLineTabs,
  PIPELINE_PARAMETERS_CONFIG,
  screenWiseConfig,
} from "../../../util/NewPipelineConfig"
import { INewPipeLineTab } from "../../../util/interface"
import PipelineTableWrapper from "./PipelineTableWrapper"

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}))

const useStyles = createUseStyles({
  nextBtn: {
    borderRadius: "7px",
    background: "var(--primary-dp-color)",
    height: "27px",
    width: "fit-content",
    paddingLeft: 10,
    paddingRight: 10,
    color: "#FFF",
    fontFamily: "Roboto",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  backBtn: {
    borderRadius: "7px",
    height: "27px",
    width: "50px",
    color: "black",
    fontSize: "12px",
    fontStyle: "normal",
    lineHeight: "normal",
    textDecoration: "underline",
    fontWeight: 500,
  },
  title: {
    color: "#000",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
  },
  bottomWrapper: {
    display: "grid",
    gridTemplateColumns: "0.6fr 1fr",
    alignItems: "center",
    justifyItems: "center",
  },
  note: {
    fontSize: "10px",
    fontStyle: "italic",
    fontWeight: 300,
    lineHeight: "12.19px",
    textAlign: "left",
  },
  btnWrapper: {
    display: "flex",
    justifyContent: "flex-end",
  },
  iconButton: {
    padding: 5,
  },
  backdrop: {
    color: "#fff",
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  disabledBtn: {
    backgroundColor: "lightgray",
  },
  enabledBtn: {
    backgroundColor: "var(--primary-dp-color)",
  },
})

export default function NewPipeLineDialog() {
  const isModalOpen = useAppSelector(modelOpenSelector)
  const parameterData = useAppSelector(getParameterData)

  const dispatch = useAppDispatch()

  // Handle dialog close events
  const handleClose = (event: any, reason: string) => {
    if (reason && reason === "backdropClick" && "escapeKeyDown") return
    dispatch(closeDialog())
  }

  const theme: any = useTheme()
  const classes = useStyles({ theme })

  // Selector to get various pieces of state
  const activeScreen = useAppSelector(
    (state) => state.monitor.newPipeLine.activeScreen,
  )
  const allConfigData = useAppSelector(
    (state) => state.monitor.newPipeLine.tableData,
  )
  const runPipeLineStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.runPipeLineStatus,
  )
  const newPipeLineConfigUpdateStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.status,
  )
  const scheduelPipeLineStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.scheduelPipeLineStatus,
  )
  const pipeLineListInfoStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.pipeline.status,
  )
  const useCaseStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.useCase.status,
  )
  const marketStatus = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.market.status,
  )
  const pipelineSelectedValue = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.pipeline.selectedValue,
  )
  const marketSelectedValue = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.market.selectedValue,
  )
  const useCaseSelectedValue = useAppSelector(
    (state) => state.monitor.newPipeLine.filters.useCase.selectedValue,
  )
  const userChangedTabs = useAppSelector(
    (state) => state.monitor.newPipeLine.userChangedTabs,
  )
  const time = useAppSelector(
    (state) => state.monitor.newPipeLine.schedule.time,
  )
  const scheduledDate = useAppSelector(
    (state) => state.monitor.newPipeLine.schedule.date,
  )
  const scheduledTimeZone = useAppSelector(
    (state) => state.monitor.newPipeLine.schedule.timeZone,
  )
  const scheduleNow = useAppSelector(
    (state) => state.monitor.newPipeLine.scheduleNow,
  )
  const runNow = useAppSelector((state) => state.monitor.newPipeLine.runNow)

  // Local state to control button enable/disable
  const [disableNextBtn, setDisableNextBtn] = React.useState(true)
  const activeTab = useAppSelector(activeTabSelector)

  // Effect to manage button state based on selected values
  React.useEffect(() => {
    if (activeScreen !== NEW_PIPELINE_SCREENS.SCHEDULE) {
      if (
        !pipelineSelectedValue ||
        !useCaseSelectedValue ||
        !marketSelectedValue
      ) {
        setDisableNextBtn(true)
      } else {
        setDisableNextBtn(false)
      }
    }
  }, [
    useCaseSelectedValue,
    marketSelectedValue,
    pipelineSelectedValue,
    activeTab,
    activeScreen,
  ])

  // Effect to manage button state for scheduling
  React.useEffect(() => {
    if (activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE && scheduleNow) {
      if (!time || !scheduledDate || !scheduledTimeZone) {
        setDisableNextBtn(true)
      } else {
        setDisableNextBtn(false)
      }
    } else if (activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE && runNow) {
      setDisableNextBtn(false)
    }
  }, [
    time,
    scheduledDate,
    scheduledTimeZone,
    scheduleNow,
    runNow,
    activeTab,
    activeScreen,
  ])

  // Function to handle screen transitions
  const handleScreenChange = async () => {
    if (activeScreen === NEW_PIPELINE_SCREENS.DETAILS) {
      dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.PARAMETERS))
    } else if (activeScreen === NEW_PIPELINE_SCREENS.PARAMETERS) {
      handleUpdateParameters()
    } else if (activeScreen === NEW_PIPELINE_SCREENS.CONFIG) {
      const index = findIndexByKey(activeTab)

      if (index === NewPipeLineTabs.length - 1) {
        dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.SCHEDULE))
        const tabKeys = Object.keys(allConfigData)
        tabKeys.map((key) => {
          if (allConfigData?.[key]?.length && userChangedTabs.includes(key)) {
            if (key === "Input Series Config") {
              const temp = inputSeriesDateConversionBeforeSave(
                allConfigData[key],
              )
              dispatch(
                newPipeLineUpdateBaseDataAsync({
                  data: temp,
                  fileType: newPipeLineMapper[key],
                }),
              )
              return
            }
            dispatch(
              newPipeLineUpdateBaseDataAsync({
                data: allConfigData[key],
                fileType: newPipeLineMapper[key],
              }),
            )
          }
        })
      } else {
        dispatch(updateTabIndex(NewPipeLineTabs[index + 1].key))
      }
    } else if (activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE) {
      if (runNow) {
        await dispatch(runPipeLineAsync())
        setTimeout(() => {
          refresh()
        }, 12000)
      } else {
        const providedTime = mergeDateAndTime(
          new Date(scheduledDate),
          new Date(time),
        )
        if (isPast(providedTime)) {
          dispatch(
            setAlertData2(
              setAlertData(
                ALERT_MSGS.PIPELINE.PAST_TIME,
                ALERT_STATUS_MSGS.ERROR,
                true,
              ),
            ),
          )
        } else {
          dispatch(schedulePipeLineAsync())
        }
      }
    }
  }

  // Function to refresh pipeline status
  const refresh_status = async () => {
    try {
      const status_data = await dispatch(refreshStatusPipeLineAsync())
      return status_data.payload.is_running
    } catch (error) {
      console.error("Error:", error)
    }
  }

  const refreshStatus = useAppSelector(
    (store) => store.monitor.newPipeLine.refreshInprogress,
  )

  // Main refresh function for the pipeline
  const refresh = async () => {
    if (refreshStatus) {
      return
    }
    let is_running = await refresh_status()
    if (is_running === true) {
      dispatch(
        setAlertData2(
          setAlertData(
            "Data refresh is already running!",
            ALERT_STATUS_MSGS.INFO,
            true,
          ),
        ),
      )
      dispatch(updateRefreshStatus(true))
    } else {
      const data = await dispatch(refreshPipeLineAsync())
      dispatch(updateRefreshStatus(true))
      const message = data.payload.message
      dispatch(
        setAlertData2(setAlertData(message, ALERT_STATUS_MSGS.SUCCESS, true)),
      )
    }
    await new Promise((resolve) => setTimeout(resolve, 10000))
    is_running = await refresh_status()
    while (is_running) {
      await new Promise((resolve) => setTimeout(resolve, 60000))
      is_running = await refresh_status()
    }
    dispatch(lastRunAsync())
    dispatch(gantChartAsync())
    dispatch(pipelineCardsAsync())
    dispatch(updateRefreshStatus(false))
    dispatch(
      setAlertData2(
        setAlertData(
          "Data refreshed successfully!",
          ALERT_STATUS_MSGS.SUCCESS,
          true,
        ),
      ),
    )
  }

  // Handle navigation back to the previous screen
  const handleBack = () => {
    if (activeScreen === NEW_PIPELINE_SCREENS.PARAMETERS) {
      dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.DETAILS))
    } else if (activeScreen === NEW_PIPELINE_SCREENS.CONFIG) {
      const index = findIndexByKey(activeTab)
      if (index !== 0) {
        dispatch(updateTabIndex(NewPipeLineTabs[index - 1].key))
      } else {
        dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.PARAMETERS))
      }
    } else {
      dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.CONFIG))
    }
  }

  const handleChangeConfigParameter = (event: React.SyntheticEvent) => {
    dispatch(toggleNewPipeLineScreen(event.currentTarget.textContent))
  }

  const handleUpdateParameters = async () => {
    try {
      const payloadForUpdateParameter = flattenParamsToKeyValue(parameterData)
      const payload = {
        pipeline_name: pipelineSelectedValue,
        new_default_values: payloadForUpdateParameter,
      }

      const resultAction = await dispatch(
        updateParametersForPipelineAsync(payload),
      )

      if (updateParametersForPipelineAsync.fulfilled.match(resultAction)) {
        dispatch(
          setAlertData2(
            setAlertData(
              "Parameters updated successfully!",
              ALERT_STATUS_MSGS.SUCCESS,
              true,
            ),
          ),
        )
        dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.CONFIG))
      } else {
        throw new Error("Update rejected")
      }
    } catch (error) {
      dispatch(
        setAlertData2(
          setAlertData(
            "Failed to update parameters.",
            ALERT_STATUS_MSGS.ERROR,
            true,
          ),
        ),
      )
    }
  }

  return (
    <div>
      {/* Modal for New Pipeline Workflow */}
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={isModalOpen}
        fullWidth={true}
        maxWidth={
          activeScreen === NEW_PIPELINE_SCREENS.DETAILS ||
          activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE
            ? "sm"
            : "lg"
        }
      >
        {/* Dialog Header */}
        <DialogTitle
          id="customized-dialog-title"
          sx={{
            padding:
              activeScreen != NEW_PIPELINE_SCREENS.CONFIG ||
              activeScreen != NEW_PIPELINE_SCREENS.PARAMETERS
                ? "10px"
                : "5px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className={classes.title}
        >
          <Box>{screenWiseConfig.title[activeScreen]}</Box>
          <IconButton
            aria-label="close"
            onClick={(e) => handleClose(e, "close")}
            className={classes.iconButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content based on the active screen */}
        <DialogContent
          dividers={
            activeScreen !== NEW_PIPELINE_SCREENS.CONFIG ||
            activeScreen !== NEW_PIPELINE_SCREENS.PARAMETERS
          }
        >
          {activeScreen === NEW_PIPELINE_SCREENS.DETAILS ? (
            <NewPipeLineDetails />
          ) : null}
          {activeScreen === NEW_PIPELINE_SCREENS.CONFIG ||
          activeScreen === NEW_PIPELINE_SCREENS.PARAMETERS ? (
            <>
              <ThemeProvider theme={redThemedTabs}>
                <Tabs
                  value={activeScreen}
                  onChange={handleChangeConfigParameter}
                  textColor="primary"
                >
                  {PIPELINE_PARAMETERS_CONFIG.map((obj: INewPipeLineTab) => {
                    return (
                      <Tab
                        sx={{
                          textTransform: "none",
                        }}
                        value={obj.label}
                        label={obj.label}
                        key={obj.key}
                      />
                    )
                  })}
                </Tabs>
              </ThemeProvider>
              <PipelineTableWrapper
                parameterOrConfig={activeScreen}
                pipelineSelectedValue={pipelineSelectedValue}
              />
            </>
          ) : null}
          {activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE ? (
            <ScheduleTheRun />
          ) : null}

          {/* Backdrop shown during loading */}
          <Backdrop
            className={classes.backdrop}
            open={
              runPipeLineStatus === API_STATUS.LOADING ||
              newPipeLineConfigUpdateStatus === API_STATUS.LOADING ||
              scheduelPipeLineStatus === API_STATUS.LOADING ||
              pipeLineListInfoStatus === API_STATUS.LOADING ||
              useCaseStatus === API_STATUS.LOADING ||
              marketStatus === API_STATUS.LOADING
            }
          >
            <CircularProgress />
          </Backdrop>
        </DialogContent>

        {/* Footer actions for navigation */}
        <DialogActions className={classes.dialogActions}>
          <Box
            className={classes.bottomWrapper}
            sx={{
              gridTemplateColumns:
                activeScreen === NEW_PIPELINE_SCREENS.DETAILS
                  ? "1fr !important"
                  : "inherit",
            }}
          >
            {
              <>
                {activeScreen !== NEW_PIPELINE_SCREENS.DETAILS && (
                  <button className={classes.backBtn} onClick={handleBack}>
                    Back
                  </button>
                )}

                {/* Next or Submit button based on the active screen */}
                {activeScreen === NEW_PIPELINE_SCREENS.SCHEDULE ? (
                  <button
                    className={`${classes.nextBtn} ${
                      disableNextBtn ? classes.disabledBtn : classes.enabledBtn
                    }`}
                    onClick={handleScreenChange}
                    disabled={disableNextBtn}
                  >
                    {runNow ? "Run" : "Submit"}
                  </button>
                ) : (
                  <>
                    <button
                      className={`${classes.nextBtn} ${
                        disableNextBtn
                          ? classes.disabledBtn
                          : classes.enabledBtn
                      }`}
                      onClick={handleScreenChange}
                      disabled={disableNextBtn}
                    >
                      {screenWiseConfig.btn[activeScreen]}
                    </button>
                  </>
                )}
              </>
            }
          </Box>
        </DialogActions>
      </BootstrapDialog>
    </div>
  )
}

function mergeDateAndTime(date: any, dateWithTime: any) {
  const year = getYear(date)
  const month = getMonth(date)
  const day = getDate(date)
  const hours = getHours(dateWithTime)
  const minutes = getMinutes(dateWithTime)
  const seconds = getSeconds(dateWithTime)

  return new Date(year, month, day, hours, minutes, seconds)
}
